import React, { useEffect, useState } from "react";
import { api, apiJson, getToken } from "@/api/client.js";
import { GroupMap } from "@/components/GroupMap.jsx";
import { PointVersions } from "@/components/HuntMorning.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

const KML = "Orignal2026_9_points_Eric_NordQuest.kml";
const GPX = "Orignal2026_9_points_Eric_NordQuest.gpx";
const FALLBACK_POINTS = [
  { id: "pt_nq_01", name: "Beau spot Eric love" },
  { id: "pt_nq_02", name: "Belle cache tr\u00e8s longue shot facile d\u2019acc\u00e8s" },
  { id: "pt_nq_03", name: "Buchage \u00e0 blanc" },
  { id: "pt_nq_04", name: "Cache \u00e0 dan" },
  { id: "pt_nq_05", name: "Cam\u00e9ra du bout" },
  { id: "pt_nq_06", name: "Chalet neuf pas de piste" },
  { id: "pt_nq_07", name: "Corde et cam\u00e9ra" },
  { id: "pt_nq_08", name: "Le vieux chack" },
  { id: "pt_nq_09", name: "Trail 4 roues" },
];

function armDownload() {
  const token = getToken();
  if (!token) return;
  document.cookie = `orignal_token=${token}; Path=/api/nordquest/file; Max-Age=600; SameSite=Lax`;
}

export function NordQuestPage() {
  const { ui } = useOrignalUi();
  const { user } = useAuth();
  const nq = ui.nordquest;
  const [data, setData] = useState(null);
  const [chalet, setChalet] = useState(false);
  const [focus, setFocus] = useState("");

  async function reload() {
    setData(await api("/api/nordquest"));
  }
  useEffect(() => {
    armDownload();
    reload().catch(() => {});
  }, []);

  const hunters = data?.hunters || [];
  const points = data?.points?.length ? data.points : FALLBACK_POINTS;
  const mineId = user?.member_id || user?.member?.id;
  const mine = hunters.find((h) => h.member_id === mineId);
  const isHunter = Boolean(mine);

  async function patch(memberId, body) {
    const res = await apiJson(`/api/nordquest/status/${memberId}`, body, "PATCH");
    if (res?._offline) return;
    await reload();
  }

  function flag(member, key) {
    if (!member?.can_edit) {
      return <span className={member?.[key] ? "og-nq__ok" : ""}>{member?.[key] ? nq.yes : nq.no}</span>;
    }
    const on = Boolean(member[key]);
    return (
      <button
        type="button"
        className={`og-nq__toggle${on ? " is-on" : ""}`}
        data-testid={`nq-flag-${member.member_id}-${key}`}
        onClick={() => patch(member.member_id, { [key]: !on })}
      >
        {on ? nq.yes : nq.no}
      </button>
    );
  }

  const rows = [
    ["installed", nq.rowInstalled],
    ["map_opened", nq.rowMap],
    ["points_imported", nq.rowPoints],
    ["offline_ready", nq.rowOffline],
  ];

  return (
    <main className="og-nq" data-testid="page-nordquest" data-surface="dark">
      <header className="og-nq__summary" data-testid="nq-summary">
        <p className="og-kicker">{nq.groupTitle}</p>
        <p className="og-nq__ready" data-testid="nq-ready-count">
          <strong>{data?.ready_count ?? 0} / {data?.hunter_count ?? 6}</strong> {nq.readyOf}
        </p>
        <p className="og-nq__facts">
          <span>{nq.pointsOfficial}</span>
          <span>{nq.sector}</span>
          <span>{nq.offline} {data?.offline_ready_count ?? 0}/6</span>
          <span>{nq.updated} {data?.updated_at ? data.updated_at.slice(0, 16).replace("T", " ") : nq.noneYet}</span>
        </p>
        <p data-testid="nq-version">{nq.versionLine} · {nq.version}</p>
        <p className="og-hint" data-historical="prospect" data-testid="nq-history">{nq.history}</p>
      </header>

      <section className="og-nq__step">
        <button type="button" className={`og-btn${chalet ? " og-btn--primary" : ""}`} data-testid="nq-chalet" onClick={() => { setChalet((v) => !v); setFocus(""); }}>
          {nq.chalet}
        </button>
        {chalet ? (
          <div data-testid="nq-chalet-board">
            <p className="og-hint">{nq.chaletHint}</p>
            <ul className="og-nq__chalet">
              {hunters.map((h) => (
                <li key={h.member_id}>
                  <button type="button" data-testid={`nq-chalet-${h.member_id}`} onClick={() => setFocus(focus === h.member_id ? "" : h.member_id)}>
                    <span>{h.first_name}</span>
                    <span className={h.ready ? "og-nq__ok" : ""}>{h.ready ? "✅" : "⏳"}</span>
                  </button>
                  {focus === h.member_id ? (
                    <ul>
                      {rows.filter(([key]) => !h[key]).map(([, label]) => <li key={label}>{label}</li>)}
                      {h.ready ? <li className="og-nq__ok">{nq.rowReady}</li> : null}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <header>
        <h1>{nq.title}</h1>
        <p>{nq.subtitle}</p>
      </header>

      <section className="og-nq__step" data-testid="nq-step-1">
        <h2>{nq.step1}</h2>
        <div className="og-nq__stores">
          <a className="og-btn og-btn--primary" data-testid="nq-app-store" href={data?.stores?.ios || "https://apps.apple.com/ca/app/nordquest/id6759458166"} target="_blank" rel="noreferrer">{nq.appStore}</a>
          <a className="og-btn og-btn--primary" data-testid="nq-play" href={data?.stores?.android || "https://play.google.com/store/apps/details?id=ca.pulsarinformatique.nordquest"} target="_blank" rel="noreferrer">{nq.play}</a>
        </div>
        {isHunter ? (
          <button type="button" className={`og-nq__toggle${mine.installed ? " is-on" : ""}`} data-testid="nq-installed" onClick={() => patch(mine.member_id, { installed: !mine.installed })}>
            {mine.installed ? nq.installed : nq.installedOff}
          </button>
        ) : null}
      </section>

      <section className="og-nq__step" data-testid="nq-step-2">
        <h2>{nq.step2}</h2>
        <p>{nq.step2how}</p>
        <p><strong>{nq.step2sector}</strong></p>
        {isHunter ? (
          <button type="button" className={`og-nq__toggle${mine.map_opened ? " is-on" : ""}`} data-testid="nq-map-open" onClick={() => patch(mine.member_id, { map_opened: !mine.map_opened })}>
            {mine.map_opened ? nq.mapOpen : nq.mapOpenOff}
          </button>
        ) : null}
      </section>

      <section className="og-nq__step" data-testid="nq-step-3">
        <h2>{nq.step3}</h2>
        <a className="og-btn og-btn--primary og-nq__download" data-testid="nq-download-kml" href={`/api/nordquest/file/${KML}`} download={KML} onClick={armDownload}>
          {nq.download}
        </a>
        <a className="og-nq__fallback" data-testid="nq-download-gpx" href={`/api/nordquest/file/${GPX}`} download={GPX} onClick={armDownload}>
          {nq.gpxFallback}
        </a>
      </section>

      <section className="og-nq__step" data-testid="nq-step-4">
        <h2>{nq.step4}</h2>
        <p><strong>{nq.importTitle}</strong></p>
        <ol className="og-nq__import">
          {nq.importSteps.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </section>

      <section className="og-nq__step" data-testid="nq-step-5">
        <h2>{nq.step5}</h2>
        <p>{nq.verifyTitle}</p>
        <p><strong>{nq.verifyLead}</strong></p>
        <ol className="og-nq__names" data-testid="nq-point-list">
          {points.map((p) => <li key={p.id || p.name}>{p.name}</li>)}
        </ol>
        {isHunter ? (
          <button type="button" className={`og-nq__toggle${mine.points_imported ? " is-on" : ""}`} data-testid="nq-points-seen" onClick={() => patch(mine.member_id, { points_imported: !mine.points_imported })}>
            {mine.points_imported ? nq.seeNine : nq.seeNineOff}
          </button>
        ) : null}
      </section>

      <section className="og-nq__step" data-testid="nq-step-6">
        <h2>{nq.step6}</h2>
        <p><strong>{nq.woods}</strong></p>
        <p>{nq.woodsHow}</p>
        {isHunter ? (
          <button type="button" className={`og-nq__toggle${mine.offline_ready ? " is-on" : ""}`} data-testid="nq-offline" onClick={() => patch(mine.member_id, { offline_ready: !mine.offline_ready })}>
            {mine.offline_ready ? nq.offlineReady : nq.offlineTodo}
          </button>
        ) : <p className="og-hint">{nq.notHunter}</p>}
        {isHunter ? (
          <button type="button" className="og-btn og-btn--primary og-nq__download" data-testid="nq-ready" onClick={() => patch(mine.member_id, { ready: true })}>
            {nq.imReady}
          </button>
        ) : null}
      </section>

      <section className="og-nq__step" data-testid="nq-track">
        <h2>{nq.track}</h2>
        {hunters.map((h) => (
          <article key={h.member_id} className="og-nq__hunter" data-testid={`nq-hunter-${h.member_id}`}>
            <header>
              <strong>{h.first_name}</strong>
              <span className={h.ready ? "og-nq__ok" : ""}>{nq.rowReady} {h.ready ? nq.yes : nq.no}</span>
            </header>
            {rows.map(([key, label]) => (
              <div key={key} className="og-nq__row">
                <span>{label}</span>
                {flag(h, key)}
              </div>
            ))}
            <div className="og-nq__row" data-testid={`nq-ready-row-${h.member_id}`}>
              <span>{nq.rowReady}</span>
              <span className={h.ready ? "og-nq__ok" : ""}>{h.ready ? nq.yes : nq.no}</span>
            </div>
          </article>
        ))}
        <p className="og-hint">{nq.associates}</p>
      </section>

      <GroupMap />
      <PointVersions />
      <figure className="og-nq__qr" data-surface="light" data-testid="nq-qr-block">
        <img src="/api/nordquest/qr.svg" alt={nq.qrCaption} data-testid="nq-qr" width="168" height="168" />
        <figcaption>{nq.qrCaption}</figcaption>
      </figure>
    </main>
  );
}
